import { type ApiStorageTransaction, EnumStorageTransactionStatus, EnumStorageTransactionType } from '@Model';
import { EnumStatus, isLinked } from '@synqo/frontend-component-library';

export type BookingState = 'effective' | 'cancelled' | 'counter';

type CancellableFields = Pick<
    ApiStorageTransaction,
    'status' | 'type' | 'transaction_status' | 'reverses_transaction_id' | 'origin_order'
>;

const BOOKING_STATE_LABELS: Record<BookingState, string> = {
    effective: 'wirksam',
    cancelled: 'storniert',
    counter: 'Gegenbuchung',
};

/** Whether a booking counts, was cancelled, or is the counter booking of a cancellation. */
// @ai-generated 2026-10-26T15:00:00+00:00 by matthias.gessinger@synqony.com using Claude Sonnet 5.5
export function bookingState(
    booking: Pick<CancellableFields, 'transaction_status' | 'reverses_transaction_id'>,
): BookingState {
    if (booking.reverses_transaction_id) {
        return 'counter';
    }
    return booking.transaction_status === EnumStorageTransactionStatus.Cancelled ? 'cancelled' : 'effective';
}

/** The words for a state: wirksam, storniert or Gegenbuchung. */
export const bookingStateLabel = (state: BookingState) => BOOKING_STATE_LABELS[state];

/** Only an effective, stored booking that no order position made can be cancelled, and only with the Update permission. */
// @ai-generated 2026-10-26T15:00:00+00:00 by matthias.gessinger@synqony.com using Claude Sonnet 5.5
export function canCancel(booking: CancellableFields, hasUpdatePermission: boolean): boolean {
    return (
        hasUpdatePermission &&
        booking.status !== EnumStatus.Draft &&
        !isLinked(booking.origin_order) &&
        bookingState(booking) === 'effective'
    );
}

/** Whether the view says that a stored booking comes from an order position. */
// @ai-generated 2026-10-30T09:00:00Z by matthias.gessinger@synqony.com using Claude Sonnet 5.5
export function showsOrderNotice(booking: CancellableFields): boolean {
    return booking.status !== EnumStatus.Draft && isLinked(booking.origin_order);
}

/** Whether the detail view shows what the booking did to the stock: effective Zugang or Abgang. */
// @ai-generated 2026-10-26T15:00:00+00:00 by matthias.gessinger@synqony.com using Claude Sonnet 5.5
export function showsStockEffect(booking: CancellableFields): boolean {
    return (
        booking.status !== EnumStatus.Draft &&
        booking.type !== EnumStorageTransactionType.Stocktaking &&
        bookingState(booking) === 'effective'
    );
}
